import {SiDhl, SiInstagram, SiMastercard, SiUps, SiVisa} from 'react-icons/si'
import {HiMiniAtSymbol, HiMiniPhone} from 'react-icons/hi2'
import {Link, LinkProps} from '@tanstack/react-router'
import {FaCcPaypal} from 'react-icons/fa6'
import {useTranslations} from 'use-intl'
import {GrAmex} from 'react-icons/gr'
import {has} from 'lodash-es'
import {useMemo} from 'react'
import LanguageSelector from '~/components/i18n/LanguageSelector'
import useCookieOverlayState from '~/hooks/useCookieOverlayState'
import SpreadshopLogo from '~/components/images/SpreadshopLogo'
import useLocaleParams from '~/hooks/useLocaleParams'
import useShopConfig from '~/hooks/useShopConfig'
import Rocket from '~/components/images/Rocket'
import Logo from '~/components/images/Logo'

type LinkItem = {
    name: string
    to: LinkProps['to']
    params: LinkProps['params']
}

type ButtonItem = {
    name: string
    onClick: () => void
}

const isLinkItem = (item: LinkItem | ButtonItem): item is LinkItem => has(item, 'to')
const isButtonItem = (item: LinkItem | ButtonItem): item is ButtonItem => has(item, 'onClick')

const Footer = () => {
    const {showCookieOverlay} = useCookieOverlayState()
    const params = useLocaleParams()
    const {shop} = useShopConfig()
    const t = useTranslations('footer')

    const links = useMemo<(LinkItem | ButtonItem)[]>(() => {
        return [
            {
                name: t('links.about'),
                to: '/$lang/about',
                params,
            },
            {
                name: t('links.privacy'),
                to: '/$lang/privacy',
                params,
            },
            {
                name: t('links.legal'),
                to: '/$lang/legal',
                params,
            },
            {
                name: t('links.terms'),
                to: '/$lang/terms-and-conditions',
                params,
            },
            {
                name: t('links.cookies'),
                onClick: () => showCookieOverlay(),
            },
        ]
    }, [t, params, showCookieOverlay])

    return (
        <footer className="w-full overflow-hidden">
            <div className="max-w-480 flex justify-center items-center -mx-48 sm:-mx-40 md:mx-auto">
                <Rocket />
            </div>
            <div className="w-full bg-[#151515] 2xl:-mt-32 pb-12">
                <div className="w-full max-w-7xl mx-auto px-4 md:px-6 2xl:pt-14 space-y-10">
                    <div className=" flex items-center justify-center gap-4 flex-col md:flex-row">
                        <h2 className="sr-only">{t('powered.title')}</h2>
                        <Link className="block" to=".">
                            <Logo className="max-h-6 size-full" />
                        </Link>
                        <div>{t('powered.by')}</div>
                        <a className="block" href={t('powered.spreadshopLink')} target="_blank">
                            <SpreadshopLogo className="max-h-8 size-full" />
                            <span className="sr-only">{t('powered.visitSpreadshop')}</span>
                        </a>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 px-2 md:px-0">
                        <div className="md:col-span-2">
                            <div>
                                <h2 className="text-lg font-display font-bold mb-2">
                                    {t('links.title')}
                                </h2>
                                <ul className="grid gap-2 grid-cols-2">
                                    {links.map((link, index) => (
                                        <li key={index}>
                                            {isLinkItem(link) && (
                                                <Link
                                                    to={link.to}
                                                    params={link.params}
                                                    className="hover:text-nova transition-colors"
                                                >
                                                    {link.name}
                                                </Link>
                                            )}
                                            {isButtonItem(link) && (
                                                <button
                                                    type="button"
                                                    onClick={link.onClick}
                                                    className="hover:text-nova transition-colors"
                                                >
                                                    {link.name}
                                                </button>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="grid gap-x-2 gap-y-10 grid-cols-1 md:grid-cols-2 mt-8">
                                <div>
                                    <h2 className="text-lg font-display font-bold mb-2">
                                        {t('payment.title')}
                                    </h2>
                                    <ul className="flex flex-row gap-2">
                                        <li className="bg-white/10 rounded-lg p-2">
                                            <FaCcPaypal
                                                title="Paypal"
                                                aria-labelledby="paypal"
                                                className="size-10"
                                            />
                                            <span id="paypal" className="sr-only">
                                                Paypal
                                            </span>
                                        </li>
                                        <li className="bg-white/10 rounded-lg p-2">
                                            <SiVisa
                                                title="Visa"
                                                aria-labelledby="visa"
                                                className="size-10"
                                            />
                                            <span id="visa" className="sr-only">
                                                Visa
                                            </span>
                                        </li>
                                        <li className="bg-white/10 rounded-lg p-2">
                                            <SiMastercard
                                                title="Mastercard"
                                                aria-labelledby="mastercard"
                                                className="size-10"
                                            />
                                            <span id="mastercard" className="sr-only">
                                                Mastercard
                                            </span>
                                        </li>
                                        <li className="bg-white/10 rounded-lg p-2">
                                            <GrAmex
                                                title="American Express"
                                                aria-labelledby="amex"
                                                className="size-10"
                                            />
                                            <span id="amex" className="sr-only">
                                                American Express
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <h2 className="text-lg font-display font-bold mb-2">
                                        {t('shipping.title')}
                                    </h2>
                                    <ul className="flex flex-row gap-2">
                                        <li className="bg-white/10 rounded-lg p-2">
                                            <SiDhl
                                                title="DHL"
                                                aria-labelledby="dhl"
                                                className="size-16 -mt-3"
                                            />
                                            <span id="dhl" className="sr-only">
                                                DHL
                                            </span>
                                        </li>
                                        <li className="bg-white/10 rounded-lg p-2">
                                            <SiUps
                                                title="UPS"
                                                aria-labelledby="ups"
                                                className="size-10"
                                            />
                                            <span id="ups" className="sr-only">
                                                UPS
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="grid gap-x-2 gap-y-6 grid-cols-1 md:grid-cols-2 mt-6">
                                <div>
                                    <h2 className="text-lg font-display font-bold mb-2">
                                        {t('social.title')}
                                    </h2>
                                    <ul className="flex flex-row gap-3">
                                        <li>
                                            <a href={t('social.instagramLink')} target="_blank">
                                                <SiInstagram
                                                    title="Instagram"
                                                    aria-labelledby="insta"
                                                    className="size-8"
                                                />
                                                <span id="insta" className="sr-only">
                                                    Instagram
                                                </span>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <LanguageSelector />
                                </div>
                            </div>
                        </div>
                        <div>
                            <h2 className="text-lg font-display font-bold mb-1">
                                {t('service.title')}
                            </h2>
                            <p className="text-sm text-gray-300 mb-2">{t('service.description')}</p>
                            <p className="text-base space-y-2 mb-4">
                                <a
                                    className="flex items-center gap-2 leading-4"
                                    href={`mailto:${t('service.email')}?body=${encodeURI(
                                        t('service.emailBody', {
                                            shopDomain: 'gravity-shirts.com',
                                            shopName: shop?.name ?? '',
                                            shopId: shop?.id ?? '',
                                        })
                                    )}`}
                                >
                                    <HiMiniAtSymbol aria-hidden="true" className="size-5" />
                                    <span>{t('service.email')}</span>
                                </a>
                                <a
                                    className="flex items-center gap-2"
                                    href={`tel:${t('service.phoneNumber')}`}
                                >
                                    <HiMiniPhone aria-hidden="true" className="size-5" />
                                    <span className="flex gap-1 flex-wrap leading-4">
                                        <span>{t('service.phoneNumber')}</span>
                                        <span>({t('service.phoneTimes')})</span>
                                    </span>
                                </a>
                            </p>
                            <div className="text-sm">
                                <p className="block text-gray-300 mb-1">
                                    {t('service.information.description')}
                                </p>
                                <ul className="space-y-1">
                                    <li>
                                        {t('service.information.shopId', {
                                            shopId: new Intl.NumberFormat('fr-FR').format(
                                                Number(shop?.id)
                                            ),
                                        })}
                                    </li>
                                    <li>
                                        {t('service.information.shopName', {
                                            shopName: shop?.name ?? '',
                                        })}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    )
}

export default Footer
